import { Stack, Typography } from '@mui/material';
import { CardSubSection, Show } from '@side/pantry';
import type { MouseEventHandler } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { parseFirstLastName } from '@/common/utils/person';
import { derivePhoneNumber } from '@/common/utils/phoneNumber';
import { ContactsAutocomplete } from '@/components/ContactsAutocomplete';
import { useReferralContext } from '@/views/Referrals/common/ReferralContext';
import {
  findAgentTeamMembership,
  getFirstAgent,
  getIsSending,
  getSecondAgent,
  getSecondAgentFieldName,
} from '@/views/Referrals/utils';
import {
  baseAgentFields,
  type FieldValues,
} from '@/views/Referrals/views/NewReferral/types';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import type { ContactSuggestion } from '$contact-suggestions';
import { AgentContactForm } from './AgentContactForm';
import { AutocompletePaperComponent } from './AutocompletePaperComponent';
import { ContactActionsMenu } from './ContactActionsMenu';
import { ContactFormModal } from './ContactFormModal';
import {
  ContactModalState,
  useContactFormModal,
} from './ContactFormModal.helpers';
import { GoogleContactActions } from './GoogleContactActions';
import { ReadOnlyAgentDetails } from './ReadOnlyAgentDetails';
import {
  fetchBrokerageName,
  isValidAgentContact,
} from './SecondAgentDetails.helpers';

type SecondAgentDetailsProps = {
  onUpdate?: () => void;
};

export function SecondAgentDetails({ onUpdate }: SecondAgentDetailsProps) {
  const trackEvent = useTrackReferralsAnalytics();
  const { setValue } = useFormContext<FieldValues>();
  const { submitAsAgents } = useReferralContext();
  const [referralType, referringAgent, transactingAgent] = useWatch<
    FieldValues,
    ['referralType', 'referringAgent', 'transactingAgent']
  >({
    name: ['referralType', 'referringAgent', 'transactingAgent'],
  });
  const isSending = getIsSending({ referralType });
  const secondAgentFieldName = getSecondAgentFieldName({
    referralType,
  });
  // A referral fetched without this agent should render the same empty card as
  // one whose agent was cleared, which is what the blank fields already mean.
  const agentDetails =
    getSecondAgent({
      referralType,
      referringAgent,
      transactingAgent,
    }) ?? baseAgentFields;
  // The contact is just a container. We need to retain the same contact ID even if we change the data therein.
  const contactId = agentDetails.id;
  const isSecondAgentSelected = !!agentDetails.licenseNumber;
  const firstAgent = getFirstAgent({
    referralType,
    referringAgent,
    transactingAgent,
  });
  const selectedAgent = findAgentTeamMembership(firstAgent, submitAsAgents);
  const agentLabel = `${isSending ? 'Transacting' : 'Referring'} Agent`;

  // Check userId instead of isSideAgent because isSideAgent won't be set on the Create page
  const isSecondAgentWithSide = !!agentDetails.userId;

  const { field: secondAgentField, fieldState: secondAgentFieldState } =
    useController<FieldValues, 'transactingAgent' | 'referringAgent'>({
      name: secondAgentFieldName,
      rules: {
        validate: (value) =>
          (!!value && isValidAgentContact(value)) || 'Required',
      },
      shouldUnregister: false,
    });

  const handleClear = () => {
    secondAgentField.onChange({
      ...baseAgentFields,
      id: contactId,
    });
  };

  const modal = useContactFormModal({
    validateReferralAgent: isSending,
    defaultValues: baseAgentFields,
    onSubmit: (data) => {
      secondAgentField.onChange({ ...data, id: contactId });
      onUpdate?.();
    },
    // Dismissing the modal should only discard in-progress input from the add
    // flows; an already-selected contact (edit flow) must survive a cancel.
    onClose: () => {
      if (!isSecondAgentSelected) {
        handleClear();
      }
    },
  });

  const { existingContact, error, openModal, isFieldInvalid, ...modalProps } =
    modal;

  const handleSelect = async (contact: ContactSuggestion) => {
    const { firstName, lastName, email, contactResponse, googleId, group } =
      contact;

    // A referral agent's brokerage (LFRO) name isn't carried on the suggestion;
    // resolve it for the selected agent so the form pre-fills without prompting.
    const isReferralAgent = contactResponse?.roles?.includes('REFERRAL_AGENT');
    const referralBrokerageName =
      isReferralAgent &&
      contactResponse?.teamIdentityId &&
      contactResponse?.userIdentityId
        ? await fetchBrokerageName(
            contactResponse.teamIdentityId,
            contactResponse.userIdentityId,
          )
        : null;

    const updatedAgent = {
      ...baseAgentFields,
      id: contactId,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      // Wrapped in derivePhoneNumber to handle values from google contacts
      cellPhone: derivePhoneNumber(contactResponse?.cellPhone ?? ''),
      state: contactResponse?.state ?? '',
      licenseState: contactResponse?.licenseState ?? '',
      licenseNumber: contactResponse?.licenseNumber ?? '',
      userId: contactResponse?.userIdentityId ?? '',
      teamId: contactResponse?.teamIdentityId ?? '',
      isSideAgent: !googleId,
      googleContactId: googleId,
      brokerage: {
        ...baseAgentFields.brokerage,
        companyName:
          referralBrokerageName ??
          contactResponse?.brokerage?.companyName ??
          '',
        email:
          contactResponse?.brokerage?.email ??
          contactResponse?.brokerage?.officeEmail ??
          '',
        phone:
          contactResponse?.brokerage?.phone ??
          contactResponse?.brokerage?.cellPhone ??
          '',
        street1: contactResponse?.brokerage?.street1 ?? '',
        street2: contactResponse?.brokerage?.street2 ?? '',
        city: contactResponse?.brokerage?.city ?? '',
        state: contactResponse?.brokerage?.state ?? '',
        zip: contactResponse?.brokerage?.zip ?? '',
        licenseNumber: contactResponse?.brokerage?.licenseNumber ?? '',
      },
      supervisingBroker: {
        firstName: contactResponse?.supervisingBroker?.firstName ?? '',
        lastName: contactResponse?.supervisingBroker?.lastName ?? '',
        licenseNumber: contactResponse?.supervisingBroker?.licenseNumber ?? '',
        cellPhone: contactResponse?.supervisingBroker?.cellPhone ?? '',
        email: contactResponse?.supervisingBroker?.email ?? '',
      },
    };

    if (isValidAgentContact(updatedAgent)) {
      secondAgentField.onChange(updatedAgent);
      onUpdate?.();
    } else {
      openModal({
        state: ContactModalState.CONTACT_INCOMPLETE,
        data: updatedAgent,
      });
    }

    trackEvent(TrackReferralsEvent.SelectCounterPartyAgentSuggestion, {
      contactId: googleId ?? contactResponse?.userId,
      contactType: group,
      eventTarget: isSending
        ? 'transacting_agent_dropdown'
        : 'referring_agent_dropdown',
    });
  };

  const handleEnterManually: MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.currentTarget.matches('#enter-manually')) {
      const { firstName, lastName } = parseFirstLastName(
        agentDetails.firstName,
      );
      const newAgentDetails = {
        ...agentDetails,
        firstName,
        lastName,
      };
      openModal({
        state: ContactModalState.ADDING_CONTACT,
        data: newAgentDetails,
      });

      trackEvent(TrackReferralsEvent.ClickEnterContactManually);
    }
  };

  const handleEdit = () => {
    openModal({
      state: ContactModalState.EDITING_CONTACT,
      data: agentDetails,
    });

    trackEvent(TrackReferralsEvent.ClickEditContact, {
      contactId,
      googleContactId: agentDetails.googleContactId,
      contactUserId: agentDetails.userId,
    });
  };

  const handleRemove = () => {
    secondAgentField.onChange({
      ...baseAgentFields,
      id: contactId,
    });

    trackEvent(TrackReferralsEvent.ClickRemoveContact, {
      contactId,
      googleContactId: agentDetails.googleContactId,
      contactUserId: agentDetails.userId,
    });
  };

  const {
    value: secondAgentFieldValue = baseAgentFields,
    ref: _,
    ...secondAgentFieldProps
  } = secondAgentField;

  return (
    <CardSubSection>
      <ContactFormModal
        contactType={isSending ? 'referring agent' : 'transacting agent'}
        {...modalProps}
      >
        <AgentContactForm
          eventContainer="add_referrals_view"
          state={modalProps.state}
          errorMessage={error}
          existingContact={existingContact}
          onUseExistingContact={() =>
            openModal({ state: ContactModalState.EXISTING_CONTACT_FOUND })
          }
          isFieldInvalid={isFieldInvalid}
        />
      </ContactFormModal>
      <Show when={isSecondAgentSelected}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            {agentLabel}
          </Typography>
          <Stack direction="row" sx={{ gap: 1, alignItems: 'stretch' }}>
            <GoogleContactActions />
            <ContactActionsMenu
              canEdit={!isSecondAgentWithSide}
              onEdit={handleEdit}
              onRemove={handleRemove}
            />
          </Stack>
        </Stack>
      </Show>
      <Show
        when={!isSecondAgentSelected}
        fallback={<ReadOnlyAgentDetails agent={agentDetails} />}
      >
        <ContactsAutocomplete
          userId={selectedAgent?.userIdentityId ?? ''}
          teamId={selectedAgent?.teamIdentityId ?? ''}
          fixedHeight
          fullWidth
          highlight={!secondAgentFieldValue.firstName ? 'secondary' : undefined}
          label={agentLabel}
          required
          placeholder="Start typing to add"
          inputValue={secondAgentFieldValue.firstName}
          onClear={handleClear}
          onSelectOption={handleSelect}
          PaperComponent={AutocompletePaperComponent}
          paperProps={{
            onClick: handleEnterManually,
          }}
          freeSolo={false}
          includeReferralAgents={!isSending}
          excludeNonCompliantTeams
          helperText={secondAgentFieldState.error?.message}
          error={secondAgentFieldState.invalid}
          {...secondAgentFieldProps}
          onChange={(e) => {
            // Using setValue instead of onChange to delay validation until blur for this field.
            setValue(
              secondAgentFieldName,
              {
                ...secondAgentFieldValue,
                firstName: e.target.value,
              },
              { shouldDirty: true },
            );
          }}
        />
      </Show>
    </CardSubSection>
  );
}
